/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import { DefaultTheme } from 'styled-components/native'

import {
  BackgroundColor,
  BackgroundColorKey,
  BorderColor,
  BorderColorKey,
  IconColor,
  IconColorKey,
  TextColor,
  TextColorKey,
} from 'theme/types'

import { ButtonColor, ButtonColors, ButtonColorValue, ButtonVariant } from './types'
import { variantBaseKeysByColor, variantDisabledKeys, variantHoverKeysByColor } from './variants'

const getBackgroundColor = (
  background: BackgroundColor,
  key?: BackgroundColorKey | 'transparent'
): ButtonColorValue | undefined => {
  if (!key) return undefined
  if (key === 'transparent') return 'transparent'
  return background[key]
}

const getBaseKeys = ({
  variant,
  color,
  disabled,
}: {
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}): {
  backgroundKey?: BackgroundColorKey | 'transparent'
  textKey: TextColorKey
  iconKey: IconColorKey
  borderKey?: BorderColorKey
} => {
  const baseKeys = variantBaseKeysByColor[variant][color]
  const disabledKeys = disabled ? variantDisabledKeys[variant] : undefined

  return {
    backgroundKey: disabledKeys?.background ?? baseKeys.background,
    textKey: disabledKeys?.text ?? baseKeys.text,
    iconKey: disabledKeys?.icon ?? baseKeys.icon,
    borderKey: disabledKeys?.border ?? baseKeys.border,
  }
}

const getBaseColors = ({
  background,
  border,
  icon,
  text,
  variant,
  color,
  disabled,
}: {
  background: BackgroundColor
  border: BorderColor
  icon: IconColor
  text: TextColor
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}) => {
  const { backgroundKey, textKey, iconKey, borderKey } = getBaseKeys({
    variant,
    color,
    disabled,
  })

  const backgroundColor = getBackgroundColor(background, backgroundKey)

  return {
    backgroundColor,
    textColor: text[textKey],
    iconColor: icon[iconKey],
    borderColor: variant === 'tertiary' || !borderKey ? undefined : border[borderKey],
  }
}

const getHoverColors = ({
  background,
  border,
  icon,
  text,
  variant,
  color,
  disabled,
}: {
  background: BackgroundColor
  border: BorderColor
  icon: IconColor
  text: TextColor
  variant: ButtonVariant
  color: ButtonColor
  disabled: boolean
}) => {
  if (disabled) {
    return {
      hoverTextColor: undefined,
      hoverIconColor: undefined,
      hoverBackgroundColor: undefined,
      hoverBorderColor: undefined,
    }
  }

  const hoverKeys = variantHoverKeysByColor[variant][color]
  const hoverTextColor = hoverKeys?.text ? text[hoverKeys.text] : undefined

  return {
    hoverTextColor,
    hoverIconColor: hoverKeys?.icon ? icon[hoverKeys.icon] : undefined,
    hoverBackgroundColor: hoverKeys?.background ? background[hoverKeys.background] : undefined,
    hoverBorderColor:
      variant === 'tertiary' || !hoverKeys?.border ? undefined : border[hoverKeys.border],
  }
}

const applyTransparentSecondary = ({
  variant,
  transparent,
  disabled,
  backgroundColor,
  hoverBackgroundColor,
}: {
  variant: ButtonVariant
  transparent: boolean
  disabled: boolean
  backgroundColor?: ButtonColorValue
  hoverBackgroundColor?: ButtonColorValue
}) => {
  const isTransparentSecondary = variant === 'secondary' && transparent && !disabled

  return {
    backgroundColor: isTransparentSecondary ? 'transparent' : backgroundColor,
    hoverBackgroundColor: isTransparentSecondary ? 'transparent' : hoverBackgroundColor,
  }
}

export const getButtonColors = ({
  theme,
  variant,
  color,
  transparent,
  disabled,
}: {
  theme: DefaultTheme
  variant: ButtonVariant
  color: ButtonColor
  transparent: boolean
  disabled: boolean
}): ButtonColors => {
  const { background, border, icon, text } = theme.designSystem.color

  const baseColors = getBaseColors({
    background,
    border,
    icon,
    text,
    variant,
    color,
    disabled,
  })
  const hoverColors = getHoverColors({
    background,
    border,
    icon,
    text,
    variant,
    color,
    disabled,
  })
  const transparency = applyTransparentSecondary({
    variant,
    transparent,
    disabled,
    backgroundColor: baseColors.backgroundColor,
    hoverBackgroundColor: hoverColors.hoverBackgroundColor,
  })

  return {
    backgroundColor: transparency.backgroundColor ?? baseColors.backgroundColor,
    borderColor: baseColors.borderColor,
    textColor: baseColors.textColor,
    iconColor: baseColors.iconColor,
    hoverTextColor: hoverColors.hoverTextColor,
    hoverIconColor: hoverColors.hoverIconColor,
    hoverBackgroundColor: transparency.hoverBackgroundColor ?? hoverColors.hoverBackgroundColor,
    hoverBorderColor: hoverColors.hoverBorderColor,
  }
}
